import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { toast } from '@/hooks/use-toast';
import { Plus, Pencil, Trash2, Loader2, Search } from 'lucide-react';

interface ToolCategory {
  id: string;
  key: string;
  name: string;
  sort_order: number;
  created_at: string;
}

function useToolCategories() {
  return useQuery({
    queryKey: ['ai_tool_categories'],
    queryFn: async () => {
      const { data, error } = await supabase.from('ai_tool_categories').select('*').order('sort_order');
      if (error) throw error;
      return data as ToolCategory[];
    },
  });
}

export default function ToolCategoriesTab() {
  const qc = useQueryClient();
  const { data: categories = [], isLoading } = useToolCategories();

  const createMut = useMutation({
    mutationFn: async (input: { key: string; name: string; sort_order: number }) => {
      const { error } = await supabase.from('ai_tool_categories').insert(input);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ai_tool_categories'] }),
  });
  const updateMut = useMutation({
    mutationFn: async ({ id, ...input }: { id: string; key: string; name: string; sort_order: number }) => {
      const { error } = await supabase.from('ai_tool_categories').update(input).eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ai_tool_categories'] }),
  });
  const deleteMut = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('ai_tool_categories').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ai_tool_categories'] }),
  });

  const [search, setSearch] = useState('');
  const [dialog, setDialog] = useState<{ open: boolean; item?: ToolCategory }>({ open: false });
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; id: string }>({ open: false, id: '' });
  const [form, setForm] = useState({ key: '', name: '', sort_order: '0' });

  const filtered = categories.filter(c => !search || c.name.toLowerCase().includes(search.toLowerCase()) || c.key.toLowerCase().includes(search.toLowerCase()));

  const openDialog = (item?: ToolCategory) => {
    setForm(item ? { key: item.key, name: item.name, sort_order: String(item.sort_order) } : { key: '', name: '', sort_order: String(categories.length) });
    setDialog({ open: true, item });
  };

  const save = async () => {
    if (!form.key || !form.name) return;
    const payload = { key: form.key, name: form.name, sort_order: Number(form.sort_order) || 0 };
    try {
      if (dialog.item) {
        await updateMut.mutateAsync({ id: dialog.item.id, ...payload });
      } else {
        await createMut.mutateAsync(payload);
      }
      setDialog({ open: false });
      toast({ title: dialog.item ? 'Categoría actualizada' : 'Categoría agregada' });
    } catch {
      toast({ title: 'Error al guardar', variant: 'destructive' });
    }
  };

  const confirmDelete = async () => {
    try {
      await deleteMut.mutateAsync(deleteDialog.id);
      setDeleteDialog({ open: false, id: '' });
      toast({ title: 'Categoría eliminada' });
    } catch {
      toast({ title: 'Error al eliminar', variant: 'destructive' });
    }
  };

  if (isLoading) {
    return <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input placeholder="Buscar categoría…" value={search} onChange={e => setSearch(e.target.value)} className="pl-9" />
        </div>
        <Button onClick={() => openDialog()} size="sm"><Plus className="w-4 h-4 mr-1" />Agregar</Button>
      </div>
      <p className="text-xs text-muted-foreground">{filtered.length} de {categories.length} categorías de herramientas</p>

      <div className="rounded-xl border border-border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-20">Orden</TableHead>
              <TableHead>Clave</TableHead>
              <TableHead>Nombre</TableHead>
              <TableHead className="w-24">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map(cat => (
              <TableRow key={cat.id}>
                <TableCell>{cat.sort_order}</TableCell>
                <TableCell><code className="text-xs bg-muted px-1 py-0.5 rounded">{cat.key}</code></TableCell>
                <TableCell className="font-medium">{cat.name}</TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" onClick={() => openDialog(cat)}><Pencil className="w-4 h-4" /></Button>
                    <Button variant="ghost" size="icon" onClick={() => setDeleteDialog({ open: true, id: cat.id })}><Trash2 className="w-4 h-4 text-destructive" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={dialog.open} onOpenChange={(open) => !open && setDialog({ open: false })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{dialog.item ? 'Editar Categoría' : 'Agregar Categoría'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div><Label>Clave (slug)</Label><Input value={form.key} onChange={e => setForm(f => ({ ...f, key: e.target.value.toLowerCase().replace(/\s+/g, '_') }))} placeholder="Ej: automation" /></div>
            <div><Label>Nombre</Label><Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Ej: Automatización" /></div>
            <div><Label>Orden</Label><Input type="number" value={form.sort_order} onChange={e => setForm(f => ({ ...f, sort_order: e.target.value }))} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog({ open: false })}>Cancelar</Button>
            <Button onClick={save} disabled={!form.key || !form.name}>Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => !open && setDeleteDialog({ open: false, id: '' })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar esta categoría?</AlertDialogTitle>
            <AlertDialogDescription>Las herramientas con esta categoría mantendrán su valor pero no se mostrará el nombre.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete}>Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
